'use client';

import React, { useEffect, useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { ChatState, PartnerInfo } from './ChatLayout';

interface ChatTopBarProps {
  chatState: ChatState;
  partnerInfo: PartnerInfo | null;
  sessionTimer: number;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function ChatTopBar({
  chatState,
  partnerInfo,
  sessionTimer,
}: ChatTopBarProps) {
  const [elapsed, setElapsed] = useState(sessionTimer);
  const [onlineCount, setOnlineCount] = useState<number | null>(null);
  useEffect(() => {
    const fetchOnline = async () => {
      try {
        const res = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3001'}/api/stats/online`);
        const data = await res.json();
        if (data.success) setOnlineCount(data.online);
      } catch {}
    };
    fetchOnline();
    const interval = setInterval(fetchOnline, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (chatState !== 'chatting') {
      setElapsed(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [chatState]);

  useEffect(() => {
    setElapsed(sessionTimer);
  }, [sessionTimer]);

  return (
    <header className="h-12 md:h-14 bg-card border-b border-border flex items-center px-3 md:px-4 xl:px-6 gap-2 md:gap-4 flex-shrink-0 z-20">
      {/* Logo */}
      <div className="flex items-center gap-2.5 flex-shrink-0">
        <AppLogo size={32} />
        <span className="font-bold text-lg text-foreground tracking-tight hidden sm:block">
          ChatConnect
        </span>
      </div>

      <div className="flex-1" />

      {/* Online count */}
      <div className="flex items-center gap-1 text-xs md:text-sm text-muted-foreground">
        <span className="w-2 h-2 rounded-full bg-accent pulse-dot" />
        <span className="tabular-nums font-medium text-foreground">{onlineCount !== null ? onlineCount.toLocaleString() : '—'}</span>
        <span className="hidden md:inline">online now</span>
      </div>

      {/* Session timer */}
      {chatState === 'chatting' && partnerInfo && (
        <div className="hidden sm:flex items-center gap-1.5 bg-muted px-3 py-1.5 rounded-lg text-sm">
          <svg className="w-3.5 h-3.5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="tabular-nums font-semibold text-foreground">{formatDuration(elapsed)}</span>
        </div>
      )}

      {/* Partner location */}
      {chatState === 'chatting' && partnerInfo?.location && (
        <div className="hidden lg:flex items-center gap-1.5 text-sm text-muted-foreground">
          <svg className="w-3.5 h-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          </svg>
          <span className="font-medium text-foreground">{partnerInfo.location}</span>
        </div>
      )}


      {/* Status badge */}
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
        chatState === 'chatting' ?'bg-accent/10 text-accent'
          : chatState === 'searching'|| chatState === 'connecting' ?'bg-warning/10 text-warning'
          : chatState === 'disconnected' ?'bg-destructive/10 text-destructive' :'bg-muted text-muted-foreground'
      }`}>
        <span className={`w-1.5 h-1.5 rounded-full ${
          chatState === 'chatting' ? 'bg-accent pulse-dot' :
          chatState === 'searching' || chatState === 'connecting' ? 'bg-warning pulse-dot' :
          chatState === 'disconnected'? 'bg-destructive' : 'bg-muted-foreground'
        }`} />
        {chatState === 'chatting' ? 'Connected' :
         chatState === 'searching' ? 'Searching...' :
         chatState === 'connecting' ? 'Connecting...' :
         chatState === 'disconnected'? 'Disconnected' : 'Idle'}
      </div>
    </header>
  );
}